/**
 * Lending Report Types
 *
 * Type definitions for the Lending Report feature including
 * report data, decisions, and UI component props.
 */

export type LendingDecisionType = 'APPROVED' | 'REJECTED';

export type RiskSnapshotStatus = 'GOOD' | 'MODERATE' | 'POOR';

export type EmploymentType = 'Salaried' | 'Self-Employed' | 'Unknown';

export type LendingReportTab = 'summary' | 'income' | 'obligations' | 'cashflow' | 'behavior';

export interface RiskSnapshotItem {
    label: string;
    status: RiskSnapshotStatus;
    tag: string;
}

export interface FoirTrendPoint {
    month: string;
    salary: number;
    emi: number;
    foir: number;
}

export interface MonthlyHistoryEntry {
    month?: string;
    amount?: number;
    averageAmount?: number;
    [key: string]: unknown;
}

export interface PaymentWindowEntry {
    startDay: number;
    endDay: number;
    averageAmount: number;
    confidence: number;
    counterparty?: string;
    category?: string;
    [key: string]: unknown;
}

// ─── Phase 2: Bounce Log (Feature 3) ───

export interface BounceTransaction {
    date: string;
    type: string;
    amount: number;
    narration: string;
}

export interface BounceLogEntry {
    month: string;
    totalBounceTxns: number;
    totalBounceAmount: number;
    emiBounceTxns: number;
    emiBounceAmount: number;
    bounceDetected: boolean;
    transactions: BounceTransaction[];
}

// ─── Phase 2: Enhanced EMI Windows (Feature 4) ───

export interface EnrichedEmiWindow extends PaymentWindowEntry {
    monthsLeft: number | null;
    status: 'Regular' | 'Delayed' | 'Unknown';
    percentOfIncome: number;
}

// ─── Phase 2: Salary Classification (Feature 5) ───

export type SalaryClassification = 'Normal' | 'Missed' | 'Double';

export interface ClassifiedSalaryMonth {
    month: string;
    amount: number;
    classification: SalaryClassification;
}

// ─── Phase 2: Spending Breakdown (Feature 6) ───

export type SpendingGroup = 'Fixed Expenses' | 'Variable Expenses' | 'Savings/Investment';

export interface SpendingCategory {
    group: SpendingGroup;
    categories: { name: string; amount: number }[];
    totalAmount: number;
    percentOfIncome: number;
}

// ─── Phase 2: Repayment Heatmap (Feature 8) ───

export type RepaymentStatus = 'on-time' | 'late' | 'bounced' | 'no-data';

export interface RepaymentHeatmapEntry {
    month: string;
    status: RepaymentStatus;
    emiCount: number;
    bounceCount: number;
}

// ─── Main Report Data ───

export interface LendingReportData {
    consentId: string;
    batchId: string;

    // Summary metrics
    netMonthlyIncome: number;
    foir: number;
    activeEMIs: number;
    avgClosingBalance: number;
    inflowOutflowRatio: number;
    bounceEvents: number;

    // Employment
    employmentType: EmploymentType;

    // Phase 2: Statement Period (Feature 2)
    statementPeriodFrom: string | null;
    statementPeriodTo: string | null;

    // Risk Snapshot
    riskSnapshot: RiskSnapshotItem[];

    // FOIR Trend
    foirTrend: FoirTrendPoint[];

    // Income tab
    monthlySalaryHistory: MonthlyHistoryEntry[];
    salaryWindows: PaymentWindowEntry[];
    recurringCredits: PaymentWindowEntry[];

    // Phase 2: Salary Classification (Feature 5)
    classifiedSalaryHistory: ClassifiedSalaryMonth[];

    // Obligations tab
    emiWindows: PaymentWindowEntry[];
    monthlyEmiHistory: MonthlyHistoryEntry[];
    creditCardPayments: PaymentWindowEntry[];
    sipWindows: PaymentWindowEntry[];

    // Phase 2: Enhanced EMI Windows (Feature 4)
    enrichedEmiWindows: EnrichedEmiWindow[];

    // Cashflow tab
    monthlyInflowHistory: MonthlyHistoryEntry[];
    monthlyOutflowHistory: MonthlyHistoryEntry[];
    monthlyBalanceHistory: MonthlyHistoryEntry[];

    // Phase 2: Spending Breakdown (Feature 6)
    spendingBreakdown: SpendingCategory[];

    // Behavior tab
    emiBounces: number;
    nachBounces: number;
    siBounces: number;
    emiDelays: number;
    lateFeeCharges: number;
    returnCharges: number;

    // Phase 2: Bounce Log (Feature 3)
    bounceLog: BounceLogEntry[];

    // Phase 2: Repayment Heatmap (Feature 8)
    repaymentHeatmap: RepaymentHeatmapEntry[];
}

export interface LendingDecision {
    id: number;
    consentId: string;
    decision: LendingDecisionType;
    /** User ID (FK) */
    decidedBy: string;
    /** User name resolved from user_profile */
    decidedByName: string | null;
    decidedAt: string;
    createdAt: string;
}

export interface LendingReportResponse {
    status: string;
    data: LendingReportData;
}

export interface LendingDecisionResponse {
    status: string;
    data: {
        decision: LendingDecision | null;
    };
}

export interface LendingDecisionSubmitResponse {
    status: string;
    message: string;
    data: LendingDecision;
}

// Component props

export interface LendingReportDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    consentId: string;
    consentHandle?: string;
}

export interface SummaryMetricsBarProps {
    report: LendingReportData;
}

export interface SummaryTabProps {
    report: LendingReportData;
}

export interface IncomeTabProps {
    report: LendingReportData;
}

export interface ObligationsTabProps {
    report: LendingReportData;
}

export interface CashflowTabProps {
    report: LendingReportData;
}

export interface BehaviorTabProps {
    report: LendingReportData;
}

export interface DecisionActionBarProps {
    consentId: string;
    existingDecision: LendingDecision | null;
    onDecisionSubmitted: (decision: LendingDecision) => void;
}

export interface DecisionBannerProps {
    decision: LendingDecision;
}

// Utility helpers

export const formatINR = (value: number): string => {
    if (value === 0) return '\u20B90';
    if (value >= 10000000) return `\u20B9${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `\u20B9${(value / 100000).toFixed(2)}L`;
    if (value >= 1000) return `\u20B9${(value / 1000).toFixed(1)}K`;
    return `\u20B9${value.toFixed(0)}`;
};

export const getRiskStatusColor = (status: RiskSnapshotStatus): string => {
    switch (status) {
        case 'GOOD': return 'text-green-600 bg-green-50';
        case 'MODERATE': return 'text-amber-600 bg-amber-50';
        case 'POOR': return 'text-red-600 bg-red-50';
        default: return 'text-gray-600 bg-gray-50';
    }
};

export const getDecisionColor = (decision: LendingDecisionType): string => {
    return decision === 'APPROVED'
        ? 'text-green-700 bg-green-50 border-green-200'
        : 'text-red-700 bg-red-50 border-red-200';
};
